import axios from "../helpers/bearerAxios";
import { logger } from "./loggingService";
import { useViewerStore } from "../store/store";

const baseURL = import.meta.env.VITE_BASE_URL!;

const getOpportunity = async (
    opportunitySfId: string
): Promise<GetOpportunityBySfIdResponse> => {
    try {
        return await axios
            .get(`${baseURL}/api/Opportunities/${opportunitySfId}`)
            .then(response => response.data);
    } catch (e) {
        logger.error(e);
        throw e;
    }
}

const postSitesSearch = async (
  searchValue: string,
  page: number,
  stageSelectedFilter: any[] = [],
  dateSelectedFilter: any = {},
  isDateFilterApplied: boolean,
  isStageFilterApplied: boolean,
  isAuroraOnlyFilter: boolean = false
) => {
  let dateFrom = null;
  let dateTo = null;
  let searchParam: any = searchValue;
  let isAssignToMe = false;
  let sortField = "CreatedDate";
  let sortOrder = "DESC";
  let filter = { pagenumber: page, pageSize: 20 };
  let filterRequest = [];

  //Date Filter
  if (isDateFilterApplied) {
    dateTo = dateSelectedFilter.dateTo;

    //dateTo should be 1 day greater than dateTo to get the data for the selected date
    const dateToObj = new Date(dateTo);
    dateToObj.setDate(dateToObj.getDate() + 1);
    dateTo = dateToObj.toISOString();

    filterRequest.push(
      {
        field_Name: "CreatedDate",
        field_Value: mapDateFromToRequest(dateSelectedFilter.dateFrom),
        filter_Condition: "GREATER THAN",
        field_Type: "DATE",
      },
      {
        field_Name: "CreatedDate",
        field_Value: dateTo,
        filter_Condition: "LESS THAN",
        field_Type: "DATE",
      }
    );
  }

  //Stage Filter
  if (isStageFilterApplied) {
    stageSelectedFilter.forEach((item) => {
      filterRequest.push({
        field_Name: "Stage",
        field_Value: item,
        filter_Condition: "CONTAINS",
        field_Type: "STRING",
      });
    });
  }

  // Data source filter
  if (isAuroraOnlyFilter) {
    filterRequest.push({
      field_Name: "Data_Source",
      field_Value: "Aurora",
      filter_Condition: "CONTAINS",
      field_Type: "STRING",
    });
  }

  //check if searchValue contains "," then split the string
  if (searchValue.includes(",")) {
    searchParam = searchValue.split(",");
    searchParam = searchParam[0].trim();
  }

  try {
    return axios
      .post(baseURL + import.meta.env.VITE_SITES_TABLE_SEARCH_API_URL, {
        searchParam,
        dateFrom,
        dateTo,
        isAssignToMe,
        sortField,
        sortOrder,
        filter,
        filterRequest,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw error; // Re-throw the error to be caught by the caller
      });
  } catch (e) {
    logger.error(e);
    throw e; // Re-throw the error to be caught by the caller
  }
};

const loadSiteDesigns = (opportunityId: string, token: string) => {
  try {
    return axios
      .get(`${import.meta.env.VITE_GET_SITE_DESIGNS}${opportunityId}`,{
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then((response) => {
        const siteDesigns = {
          aurora: response?.data?.aurora?.siteJson != null
            ? response.data.aurora.siteJson
            : null,
          eagleView: response?.data?.eagleView?.statusMessage === 'SUCCESS'
            ? response.data.eagleView.body
            : null,
        };

        useViewerStore.setState({ siteDesigns: siteDesigns });
      });
  } catch (e) {
    logger.error(e);
    return e;
  }
};

export const getOneDrawEntraIdToken = async () => {
  try {
    return axios
      .post(baseURL + `${import.meta.env.VITE_ENTRA_ID_TOKEN_API_URL}`)
      .then((response) => {
        return response.data.access_token;
      });
  } catch (err) {
    return err;
  }
};


const retrieveSiteDesignDetails = async (opId: string) => {
  try {
    return axios
      .get(
        baseURL +
          import.meta.env.VITE_RETRIEVE_SITE_DESIGN_DETAILS +
          `?OpportunityId=${opId}`
      )
      .then((response) => {
        return response.data;
      });
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

const retrieveSiteDesignDetailsWithDesignData = async (
  opportunityId: string,
  designNo: number,
  versionNo: number
) => {
  try {
    const response = await axios.post(
      baseURL +
        import.meta.env.VITE_RETRIEVE_SITE_DESIGN_DETAILS_WITH_DESIGN_DATA,
      {
        opportunityId: opportunityId,
        designNo: designNo,
        versionNo: versionNo,
      }
    );
    return response.data[0];
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

const GetOldBuildingAttributes = async (
  opportunitySfId: string
): Promise<any> => {
  try {
    return await axios
      .get(`${baseURL}${import.meta.env.VITE_RETRIEVE_OLD_BUILDING_ATTRIBUTES}/${opportunitySfId}`)
      .then(response => response.data);
  } catch (e) {
    logger.error(e);
    throw e;
  }
}

const retrieveSiteDesignVersionDetails = async (payload: any) => {
  try {
    return axios
      .post(
        baseURL + import.meta.env.VITE_RETRIEVE_DESIGN_VERSION_DETAILS,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        return response.data[0];
      });
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

const saveSiteDetails = async (payload: SaveSiteDetailsRequest): Promise<SaveSiteDetailsResponse> => {
  try {
    const response = await axios.post(
      baseURL + import.meta.env.VITE_SAVE_SITE_DETAILS,
      payload,
      {
        headers: {
          "Content-Type": "application/json", // Set the content type as JSON
        },
      }
    );

    return response.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

const updateBuildingAttributes = async (payload: any) => {
  try {
    const response = await axios.post(
      baseURL + import.meta.env.VITE_UPDATE_SITE_DETAILS,
      payload
    );

    return response.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

const saveDesignCertification = async (designid: string, certification: number) => {
  try {
    return axios
      .post(
        baseURL +
        import.meta.env.VITE_UPDATE_DESIGN_CERTIFICATION_STATUS +
        `${designid}&Design_Certification_Status_Id=${certification}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        return response.data;
      });
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

const mapDateFromToRequest = (dateFrom: string) => {
  //dateFrom should be 1 day lesser than dateFrom to get the data for the selected date
  const dateFromObj = new Date(dateFrom);
  dateFromObj.setDate(dateFromObj.getDate() - 1);

  return dateFromObj.toISOString();
}

const siteService = {
  getOneDrawEntraIdToken,
  getOpportunity,
  loadSiteDesigns,
  postSitesSearch,
  retrieveSiteDesignDetails,
  saveSiteDetails,
  retrieveSiteDesignVersionDetails,
  retrieveSiteDesignDetailsWithDesignData,
  updateBuildingAttributes,
  saveDesignCertification,
  GetOldBuildingAttributes,
};

export default siteService;
